import type { DocNode, NodeType } from './schema';

/**
 * Canonical document layout (ADR-006). Turning the flat, per-parent `orderIndex`
 * node set into the order a reader sees — and the derived display facts that order
 * implies — is done HERE, once, so every renderer agrees. Two consumers rely on
 * it: the reading surface (`ReadingView`) and the Doc-builder export payload
 * (`gdoc.ts`). They diverged once — the Doc-builder iterated the raw DB order
 * (a flat `ORDER BY order_index`, which interleaves every parent's children) and
 * scrambled the output. Keeping the walk in one place is the fix.
 *
 * `orderIndex` is REAL and **per-parent** (siblings), never global — so the order
 * is a depth-first walk of the parent tree, siblings sorted by `orderIndex`, NOT
 * a flat sort.
 */

/** A node whose children read INDENTED beneath it (a leader prompt / block). */
function isLeaderType(t: NodeType): boolean {
  return t === 'spoken' || t === 'spoken-block' || t === 'alternative';
}

export interface NodeLayout {
  /** Live (non-deleted) nodes in reading order: depth-first, siblings by orderIndex. */
  ordered: DocNode[];
  /** Every live node by id (the tree index used to walk ancestors). */
  byId: Map<string, DocNode>;
  /** Computed display number for each `rubric` node (running integer, 1-based). */
  rubricNumberById: Map<string, number>;
  /** Structural indent depth = number of leader-type ancestors above the node. */
  indentLevelById: Map<string, number>;
}

/**
 * Compute the reading order + derived display facts for a document's nodes.
 * Pure: filters tombstones, never mutates the input. Display numbers are computed
 * here (never stored) per the ADR invariant, so insert/move/delete can't break a
 * cross-reference.
 */
export function layoutNodes(nodes: DocNode[]): NodeLayout {
  const live = nodes.filter((n) => !n.deleted);
  const byId = new Map(live.map((n) => [n.id, n]));

  // Group children by parent, each sibling list sorted by its REAL orderIndex.
  const byParent = new Map<string | null, DocNode[]>();
  for (const n of live) {
    const list = byParent.get(n.parentId) ?? [];
    list.push(n);
    byParent.set(n.parentId, list);
  }
  for (const list of byParent.values()) list.sort((a, b) => a.orderIndex - b.orderIndex);

  // Depth-first walk from the top level down.
  const ordered: DocNode[] = [];
  const walk = (parentId: string | null): void => {
    for (const n of byParent.get(parentId) ?? []) {
      ordered.push(n);
      walk(n.id);
    }
  };
  walk(null);

  // Rubric display numbers: a running integer in document order.
  const rubricNumberById = new Map<string, number>();
  let rn = 0;
  for (const n of ordered) if (n.type === 'rubric') rubricNumberById.set(n.id, ++rn);

  // Structural indent: how many leader-type ancestors a node sits under (a
  // congregational response nested under its leader prompt reads indented).
  const indentLevelById = new Map<string, number>();
  for (const n of ordered) {
    let level = 0;
    let anc: string | null = n.parentId;
    while (anc) {
      const p = byId.get(anc);
      if (p && isLeaderType(p.type)) level += 1;
      anc = p?.parentId ?? null;
    }
    indentLevelById.set(n.id, level);
  }

  return { ordered, byId, rubricNumberById, indentLevelById };
}
